import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Link2, Lock, LogOut, Settings, Users } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { OnlineBookingShareModal } from '@/components/booking/OnlineBookingShareDialog'
import { useAuth } from '@/contexts/AuthContext'
import { hasEffectivePermission, hasPermission, PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'

/**
 * @param {{
 *   icon: import('lucide-react').LucideIcon,
 *   label: string,
 *   onClick: () => void,
 *   destructive?: boolean,
 * }} props
 */
function MenuItem({ icon: Icon, label, onClick, destructive = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        'flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors',
        destructive ? 'text-destructive hover:bg-destructive/10' : 'text-foreground hover:bg-muted/80',
      )}
      onClick={onClick}
    >
      <Icon className={cn('h-4 w-4 shrink-0', destructive ? '' : 'text-muted-foreground')} />
      <span>{label}</span>
    </button>
  )
}

export function UserMenu() {
  const navigate = useNavigate()
  const { user, isAuthenticated, lock, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const menuRef = useRef(null)

  const displayName = user?.name ?? 'Manager'
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const canSettings = hasEffectivePermission(user, PERMISSIONS.MENU_SETTINGS)
  const canManageTeam = hasPermission(user, PERMISSIONS.ADMIN_USERS)

  function closeMenu() {
    setOpen(false)
  }

  function go(path) {
    closeMenu()
    navigate(path)
  }

  useEffect(() => {
    if (!open) {
      return undefined
    }

    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-2xl bg-white/10 py-1 pl-1 pr-2.5 text-white transition-colors hover:bg-white/15"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-gradient-to-br from-[#C9A66B] to-[#8A6D42] text-[10px] font-bold text-white">
          {initials}
        </span>
        <span className="hidden text-[12px] font-medium text-white sm:block">{displayName.split(' ')[0]}</span>
        <ChevronDown
          className={cn('hidden h-3 w-3 text-white/70 transition-transform sm:block', open && 'rotate-180')}
        />
      </button>

      {open && isAuthenticated ? (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+0.5rem)] z-[90] w-56 overflow-hidden rounded-2xl border border-border/60 bg-card p-1.5 shadow-xl"
        >
          <p className="px-3 py-2 text-xs font-medium text-muted-foreground">{displayName}</p>
          <MenuItem
            icon={Link2}
            label="Share booking link"
            onClick={() => {
              closeMenu()
              setShareOpen(true)
            }}
          />
          {canManageTeam ? (
            <MenuItem icon={Users} label="Team & roles" onClick={() => go('/admin/users')} />
          ) : null}
          {canSettings ? (
            <MenuItem icon={Settings} label="Settings" onClick={() => go('/settings')} />
          ) : null}
          <MenuItem icon={Lock} label="Lock screen" onClick={() => { closeMenu(); lock() }} />
          <div className="my-1 border-t border-border/60" />
          <MenuItem
            icon={LogOut}
            label="Sign out"
            destructive
            onClick={async () => {
              closeMenu()
              await logout()
              navigate('/login', { replace: true })
            }}
          />
        </div>
      ) : null}

      <OnlineBookingShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  )
}
